/**
 * 编辑器状态
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  DEFAULT_EDITOR_CONTEXT,
  type EditorContext,
  type EditorMode,
} from '@/types/editor-context';
import type { StoryboardBoardPreviewTrack } from '@/types/storyboard-board';

/** 资源库分类，与左侧资源面板 Tab 一致 */
export type ResourceLibraryTab = 'character' | 'location' | 'prop' | 'footage';

interface EditorState {
  // 选中状态
  selectedStoryboardId: string | null;
  selectedResourceId: string | null;
  /** 选中资源时的分类（来自资源面板），用于预览从资源库 API 取数 */
  selectedResourceType: ResourceLibraryTab | null;
  selectedStoryboardBoardItems: string[];
  selectedTagId: string | null;
  editorContext: EditorContext;

  // 播放状态
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  playbackRate: number;

  // 分镜看板状态
  storyboardBoardZoom: number;
  storyboardBoardScrollPosition: number;
  storyboardBoardExpandedPreviewTrack: StoryboardBoardPreviewTrack | null;

  // 视频生成预检浮层状态
  videoGenerationPrecheckOpen: boolean;
  videoGenerationPrecheckStoryboardId: string | null;

  // 资源关联分镜高亮状态
  linkedResourceStoryboardIds: string[] | null;
  activeLinkId: string | null;

  // 操作
  selectStoryboard: (id: string | null) => void;
  selectStoryboardOnly: (id: string | null) => void;
  selectResource: (id: string | null, resourceType?: ResourceLibraryTab | null) => void;
  selectStoryboardBoardItems: (ids: string[]) => void;
  toggleStoryboardBoardItem: (id: string) => void;
  clearStoryboardBoardSelection: () => void;
  selectTag: (tagId: string | null) => void;
  setEditorMode: (mode: EditorMode) => void;
  setEditorContext: (patch: Partial<EditorContext>) => void;
  resetEditorContext: () => void;

  setPlaying: (playing: boolean) => void;
  togglePlaying: () => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setPlaybackRate: (rate: number) => void;

  setStoryboardBoardZoom: (zoom: number) => void;
  setStoryboardBoardScrollPosition: (position: number) => void;
  setStoryboardBoardExpandedPreviewTrack: (
    track: StoryboardBoardPreviewTrack | null
  ) => void;
  toggleStoryboardBoardPreviewTrack: (track: StoryboardBoardPreviewTrack) => void;

  openVideoGenerationPrecheck: (storyboardId: string) => void;
  closeVideoGenerationPrecheck: () => void;

  setLinkedResourceStoryboardIds: (ids: string[] | null) => void;
  setActiveLinkId: (id: string | null) => void;

  reset: () => void;
}

const initialState = {
  selectedStoryboardId: null,
  selectedResourceId: null,
  selectedResourceType: null,
  selectedStoryboardBoardItems: [],
  selectedTagId: null,
  editorContext: DEFAULT_EDITOR_CONTEXT,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isMuted: false,
  playbackRate: 1,
  storyboardBoardZoom: 1,
  storyboardBoardScrollPosition: 0,
  storyboardBoardExpandedPreviewTrack: null,
  videoGenerationPrecheckOpen: false,
  videoGenerationPrecheckStoryboardId: null,
  linkedResourceStoryboardIds: null,
  activeLinkId: null,
};

export const useEditorStore = create<EditorState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        selectStoryboard: (id) =>
          set({
            selectedStoryboardId: id,
            selectedResourceId: null,
            selectedResourceType: null,
            selectedTagId: null,
            editorContext: {
              ...get().editorContext,
              mode: 'storyboard-image',
              storyboardId: id,
              resourceId: null,
              resourceType: null,
              tagId: null,
              previewItemId: null,
              dialogueId: null,
              videoId: null,
              targetVideoJobId: null,
              activeAssetType: 'image',
              resourceSelectionSource: null,
              resourceSelectionStoryboardId: null,
              resourceSelectionBoardId: null,
              resourceSelectionResourceName: null,
              resourceSelectionTagLabel: null,
            },
            // 同步更新分镜看板的选中状态
            selectedStoryboardBoardItems: id ? [id] : [],
          }),

        selectStoryboardOnly: (id) =>
          set({
            selectedStoryboardId: id,
            selectedResourceId: null,
            selectedResourceType: null,
            selectedTagId: null,
            editorContext: {
              ...get().editorContext,
              storyboardId: id,
              resourceId: null,
              resourceType: null,
              tagId: null,
              previewItemId: null,
              targetVideoJobId: null,
              resourceSelectionSource: null,
              resourceSelectionStoryboardId: null,
              resourceSelectionBoardId: null,
              resourceSelectionResourceName: null,
              resourceSelectionTagLabel: null,
            },
            // 不更新 selectedStoryboardBoardItems，保持看板选中状态
          }),

        selectResource: (id, resourceType) =>
          set({
            selectedResourceId: id,
            selectedResourceType: resourceType ?? null,
            selectedTagId: null,
            editorContext: {
              ...get().editorContext,
              resourceId: id,
              resourceType: resourceType ?? null,
              tagId: null,
              previewItemId: null,
              targetVideoJobId: null,
              resourceSelectionSource: null,
              resourceSelectionStoryboardId: null,
              resourceSelectionBoardId: null,
              resourceSelectionResourceName: null,
              resourceSelectionTagLabel: null,
            },
          }),

        selectStoryboardBoardItems: (ids) => set({ selectedStoryboardBoardItems: ids }),

        toggleStoryboardBoardItem: (id) => {
          const current = get().selectedStoryboardBoardItems;
          const isSelected = current.includes(id);
          set({
            selectedStoryboardBoardItems: isSelected
              ? current.filter((i) => i !== id)
              : [...current, id],
          });
        },

        clearStoryboardBoardSelection: () => set({ selectedStoryboardBoardItems: [] }),
        selectTag: (tagId) =>
          set({
            selectedTagId: tagId,
            editorContext: {
              ...get().editorContext,
              tagId,
            },
          }),
        setEditorMode: (mode) =>
          set({
            editorContext: {
              ...get().editorContext,
              mode,
              activeAssetType: mode === 'storyboard-video' ? 'video' : 'image',
            },
          }),
        setEditorContext: (patch) =>
          set({
            editorContext: {
              ...get().editorContext,
              ...patch,
            },
          }),
        resetEditorContext: () => set({ editorContext: DEFAULT_EDITOR_CONTEXT }),

        setPlaying: (playing) => set({ isPlaying: playing }),
        togglePlaying: () => set({ isPlaying: !get().isPlaying }),
        setCurrentTime: (time) => set({ currentTime: time }),
        setDuration: (duration) => set({ duration }),

        setVolume: (volume) => set({ volume: Math.max(0, Math.min(1, volume)), isMuted: false }),
        toggleMute: () => set({ isMuted: !get().isMuted }),
        setPlaybackRate: (rate) => set({ playbackRate: Math.max(0.25, Math.min(2, rate)) }),

        setStoryboardBoardZoom: (zoom) =>
          set({ storyboardBoardZoom: Math.max(1, Math.min(3, zoom)) }),
        setStoryboardBoardScrollPosition: (position) =>
          set({ storyboardBoardScrollPosition: position }),
        // 展开态属于临时视图状态，不进入 partialize 持久化
        setStoryboardBoardExpandedPreviewTrack: (track) =>
          set({ storyboardBoardExpandedPreviewTrack: track }),
        toggleStoryboardBoardPreviewTrack: (track) =>
          set({
            storyboardBoardExpandedPreviewTrack:
              get().storyboardBoardExpandedPreviewTrack === track ? null : track,
          }),

        openVideoGenerationPrecheck: (storyboardId) =>
          set({
            videoGenerationPrecheckOpen: true,
            videoGenerationPrecheckStoryboardId: storyboardId,
          }),
        closeVideoGenerationPrecheck: () =>
          set({
            videoGenerationPrecheckOpen: false,
            videoGenerationPrecheckStoryboardId: null,
          }),

        setLinkedResourceStoryboardIds: (ids) => set({ linkedResourceStoryboardIds: ids }),
        setActiveLinkId: (id) => set({ activeLinkId: id }),

        // 重置时保留已持久化字段（volume、playbackRate、storyboardBoardZoom），避免丢失用户偏好
        reset: () =>
          set((state) => ({
            ...initialState,
            volume: state.volume,
            playbackRate: state.playbackRate,
            storyboardBoardZoom: state.storyboardBoardZoom,
          })),
      }),
      {
        name: 'editor-store',
        // 以下字段会持久化到 localStorage，刷新后恢复（含分镜看板缩放百分比）
        partialize: (state) => ({
          volume: state.volume,
          playbackRate: state.playbackRate,
          storyboardBoardZoom: state.storyboardBoardZoom,
        }),
        // 显式合并：保证从 localStorage 恢复的值覆盖初始状态，避免刷新后仍显示 100%
        merge: (persisted, current) => ({
          ...current,
          ...(persisted && typeof persisted === 'object' && !Array.isArray(persisted)
            ? persisted
            : {}),
        }),
      }
    ),
    { name: 'EditorStore' }
  )
);
